import { useNavigate, useLocation, useParams, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { Title } from "../../types/Title";
import useTitlesService from "../../services/TitlesService";
import PageTitle from "../../components/PageTitle";
import LoadingEllipsis from "../../components/LoadingEllipsis";
import Ghost from "../../components/Ghost";
import BackButton from "../../components/BackButton";
import useSessionsService from "../../services/SessionsService";
import useUserService from "../../services/UserService";
import { LiveAppStreamingSessionWithSegments } from "../../types/Session";
import { User } from "../../types/User";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export default function AdminSessionsLiveAppDetailPage() {
  const { sessionId, userId } = useParams();
  const { getLiveAppStreamingSessionWithSegments } = useSessionsService();
  const { getTitle } = useTitlesService();
  const { getUser } = useUserService();

  const [initiated, setInitiated] = useState(false);
  const [session, setSession] = useState<LiveAppStreamingSessionWithSegments | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [title, setTitle] = useState<Title | null>(null);
  const [titleMap, setTitleMap] = useState<Record<string, Title>>({});

  const tabs = [{ name: "Session details" }, { name: "Status updates" }];

  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    if (initiated) {
      return; // Prevent re-fetching if already initiated
    }

    if (!initiated) {
      setInitiated(true);
    }

    getLiveAppStreamingSessionWithSegments(sessionId as string, userId as string)
      .then((session) => {
        setSession(session);
      })
      .catch((error) => {
        console.error("Error fetching title:", error);
      });
  }, [initiated]);

  useEffect(() => {
    if (session && session.titleId) {
      getTitle(session.titleId)
        .then((title) => {
          setTitle(title);
        })
        .catch((error) => {
          console.error("Error fetching title:", error);
        });
    }

    if (session && session.userId) {
      getUser(session.userId)
        .then((user) => {
          setUser(user);
        })
        .catch((error) => {
          console.error("Error fetching user:", error);
        });
    }

    // For multi-title sessions, resolve the name of each title in the timeline.
    if (session && session.segments && session.segments.length > 0) {
      const uniqueTitleIds = Array.from(
        new Set(
          session.segments
            .map((s) => s.titleId)
            .filter((id): id is string => !!id)
        )
      );

      Promise.all(
        uniqueTitleIds.map((id) =>
          getTitle(id)
            .then((t) => [id, t] as const)
            .catch(() => null)
        )
      ).then((entries) => {
        const map: Record<string, Title> = {};
        entries.forEach((entry) => {
          if (entry && entry[1]) {
            map[entry[0]] = entry[1];
          }
        });
        setTitleMap(map);
      });
    }
  }, [session]);

  function formatSeconds(seconds: number): string {
    const s = Math.max(0, Math.round(seconds));
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  }

  return (
    <section className="flex flex-col gap-2 divide-stone-900">
      <BackButton />
      <div>
        {/* Desktop Tabs */}
        <div className="block">
          <div className="border-b border-gray-200">
            <nav aria-label="Tabs" className="-mb-px flex">
              {tabs.map((tab, index) => (
                <a
                  key={tab.name}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedTab(index);
                  }}
                  aria-current={index === selectedTab ? "page" : undefined}
                  className={classNames(
                    index === selectedTab
                      ? "border-white text-gray-100"
                      : "border-transparent text-gray-300 hover:border-gray-300 hover:text-white",
                    "w-1/2 border-b-2 px-1 py-4 text-center text-xl"
                  )}
                >
                  {tab.name}
                </a>
              ))}
            </nav>
          </div>
        </div>
      </div>
      <div
        className={`flex flex-col gap-2 ${selectedTab === 0 ? "" : "hidden"}`}
      >
        <h1 className="text-2xl">
          {session ? `Session details` : `Loading`}{" "}
          {!session && <LoadingEllipsis />}
        </h1>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2 col-span-2 md:col-span-1">
            {session ? (
              <>
                <h2 className="text-2xl">Core information</h2>
                <div className="card p-4">
                  <dl className="flex grid grid-cols-1 gap-4 md:grid-cols-2">
                    <dt>Id:</dt>
                    <dd>{session.id}</dd>
                    <dt>Started:</dt>
                    <dd>
                      {new Date(session.startedStamp).toLocaleString("en-GB")}
                    </dd>
                    <dt>Session duration</dt>
                    <dd>{Math.round(session.duration ?? 0)} seconds</dd>
                    <dt>IP Address:</dt>
                    <dd>{session.publicAddress ?? "Unknown"}</dd>
                  </dl>
                </div>
                <h2 className="text-2xl">Device information</h2>
                <div className="card p-4">
                  <dl className="flex grid grid-cols-1 gap-4 md:grid-cols-2">
                    <dt>Device manufacturer:</dt>
                    <dd>{session.deviceManufacturer ?? "Unknown"}</dd>
                    <dt>Device brand:</dt>
                    <dd>{session.deviceBrand ?? "Unknown"}</dd>
                    <dt>Device model:</dt>
                    <dd>{session.deviceModel ?? "Unknown"}</dd>
                    <dt>Device OS version:</dt>
                    <dd>{session.deviceOsVersion ?? "Unknown"}</dd>
                    <dt>Average audio Bitrate (Kbps):</dt>
                    <dd>{session.averageAudioBitrateKbps ?? "Unknown"}</dd>
                    <dt>Average audio Bitrate (Kbps):</dt>
                    <dd>{session.averageVideoBitrateKbps ?? "Unknown"}</dd>
                    <dt>Average framerate:</dt>
                    <dd>{session.averageFramerate ?? "Unknown"}</dd>
                    <dt>Peak watchers:</dt>
                    <dd>{session.peakWatchers ?? "Unknown"}</dd>
                  </dl>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-2xl">Core information</h2>
                <Ghost additionalClasses="h-24 col-span-2 rounded" />
                <h2 className="text-2xl">Device information</h2>
                <Ghost additionalClasses="h-24 col-span-2 rounded" />
              </>
            )}
          </div>
          <div className="flex flex-col gap-2 col-span-2 md:col-span-1">
            <h2 className="text-2xl">User</h2>
            {user ? (
              <>
                <div className="card p-4">
                  <dl className="flex grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <dt>Id:</dt>
                    <dd>
                      <NavLink
                        to={`/admin/users/${user.id}`}
                        className="underline"
                      >
                        {user.id}
                      </NavLink>
                    </dd>

                    <dt>Email:</dt>
                    <dd>{user.email}</dd>

                    <dt>Given name:</dt>
                    <dd>{user.givenName}</dd>

                    <dt>Family name:</dt>
                    <dd>{user.familyName}</dd>

                    <dt>Nickname</dt>
                    <dd>{user.nickname}</dd>

                    <dt>Picture</dt>
                    <dd>
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt="User profile"
                          className="w-24 h-24 rounded-full"
                        />
                      ) : (
                        <span className="text-gray-500">No picture</span>
                      )}
                    </dd>
                  </dl>
                </div>
              </>
            ) : (
              <Ghost additionalClasses="h-24 col-span-2 rounded" />
            )}
            <h2 className="text-2xl">
              {session?.hasMultipleTitles ? "Titles played" : "Title"}
            </h2>
            {session?.hasMultipleTitles ? (
              <div className="card p-4 flex flex-col gap-3">
                {(session.segments ?? [])
                  .slice()
                  .sort((a, b) => a.segmentIndex - b.segmentIndex)
                  .map((segment) => {
                    const segmentTitle = segment.titleId
                      ? titleMap[segment.titleId]
                      : undefined;
                    return (
                      <div
                        key={segment.segmentIndex}
                        className="flex flex-col gap-1 border-b border-white/20 pb-2 last:border-0 last:pb-0"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">
                            #{segment.segmentIndex + 1}
                          </span>
                          {segment.titleId ? (
                            <NavLink
                              to={`/admin/titles/${segment.titleId}`}
                              className="underline"
                            >
                              {segmentTitle?.name ?? segment.titleId}
                            </NavLink>
                          ) : (
                            <span className="text-gray-500">No title</span>
                          )}
                        </div>
                        <dl className="grid grid-cols-2 gap-1 text-sm">
                          <dt>Started:</dt>
                          <dd>
                            {new Date(segment.startedStamp).toLocaleString(
                              "en-GB"
                            )}
                          </dd>
                          <dt>Duration:</dt>
                          <dd>{formatSeconds(segment.duration)}</dd>
                          <dt>Watch time:</dt>
                          <dd>{formatSeconds(segment.watchDuration)}</dd>
                        </dl>
                      </div>
                    );
                  })}
              </div>
            ) : title ? (
              <>
                <div className="card p-4">
                  <dl className="flex grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <dt>Id:</dt>
                    <dd>
                      <NavLink
                        to={`/admin/titles/${title.id}`}
                        className="underline"
                      >
                        {title.id}
                      </NavLink>
                    </dd>
                    <dt>Name:</dt>
                    <dd>{title.name}</dd>
                    <dt>Description:</dt>
                    <dd>{title.description}</dd>
                    <dt>Picture</dt>
                    <dd>
                      {title.igdbCover ? (
                        <img
                          src={title.igdbCover}
                          alt="Title IGDB cover"
                          className="w-48"
                        />
                      ) : (
                        <span className="text-gray-500">No picture</span>
                      )}
                    </dd>
                  </dl>
                </div>
              </>
            ) : (
              <Ghost additionalClasses="h-24 col-span-2 rounded" />
            )}
          </div>
        </div>
      </div>
      <div
        className={`flex flex-col gap-2 ${selectedTab === 1 ? "" : "hidden"}`}
      >
        <h2 className="text-2xl">Status updates</h2>
        {session ? (
          <>
            {session.statusUpdates && session.statusUpdates.length > 0 ? (
              <>
                {session.statusUpdates.map((update, index) => (
                  <div key={update.sampleTime.toString()} className="flex flex-col gap-4 border-b border-white pb-4">
                    <dl className="flex grid grid-cols-1 gap-4 md:grid-cols-2">
                      <dt>Timestamp:</dt>
                      <dd>
                        {new Date(update.sampleTime).toLocaleString("en-GB")}
                      </dd>
                      <dt>Average framerate:</dt>
                      <dd>{update.averageFramerate}</dd>
                      </dl>
                      {update.platforms && update.platforms.length > 0 ? (
                        <>
                          {update.platforms.map((platform, idx) => (
                            <dl
                              key={`platform-{idx}`}
                              className="card p-4 flex grid grid-cols-1 gap-2 md:grid-cols-2"
                            >
                              <dt>Name</dt>
                              <dd>{platform.name}</dd>
                              <dt>Watchers</dt>
                              <dd>{platform.watchers}</dd>
                              <dt>Chat interactions</dt>
                              <dd>{platform.chatInteractions}</dd>
                              <dt>Username</dt>
                              <dd>{platform.username ?? "Unknown"}</dd>
                              <dt>Url</dt>
                              <dd>{platform.url ?? "Unknown"}</dd>
                              <dt>Audio bitrate (Kbps)</dt>
                              <dd>{platform.audioBitrateKbps ?? "Unknown"}</dd>
                              <dt>Video bitrate (Kbps)</dt>
                              <dd>{platform.videoBitrateKbps ?? "Unknown"}</dd>
                            </dl>
                          ))}
                        </>
                      ) : (<span>"Unknown"</span>)}
                      
                  </div>
                ))}
              </>
            ) : (
              <div className="card p-4">
                <p className="text-gray-500">No status updates available.</p>
              </div>
            )}
          </>
        ) : (
          <Ghost additionalClasses="h-24 col-span-2 rounded" />
        )}
      </div>
    </section>
  );
}
